import { CssBaseline, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLocation, useRouteError } from "@remix-run/react";
import { withSentry, captureRemixErrorBoundaryError } from "@sentry/remix";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useContext, useEffect } from "react";
import * as AuthService from "~/services/AuthService";
import { TempPasswordProvider } from "./routes/forgot-password/context/TempPasswordContext";
import { ThemeSwitchProvider, themeSwitchContext } from "./utils/ThemeSwitchContext";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 0, refetchOnWindowFocus: false } },
});

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" style={{ height: "100%" }}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <title>Educator Passport</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap" rel="stylesheet" />
        <Meta />
        <Links />
      </head>
      <body style={{ height: "100%" }}>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function AppContent() {
  const themeSwitch = useContext(themeSwitchContext);
  const location = useLocation();

  const injectTheme = async () => {
    const { data: user } = await AuthService.getUser();
    if (themeSwitch.updateTheme && user) {
      themeSwitch.updateTheme(
        `#${user.institute.primary_color}`,
        `#${user.institute.secondary_color}`,
        `#${user.institute.primary_light_color}`,
      );
    }
  };

  useEffect(() => {
    injectTheme();
  }, [location]);

  return (
    <ThemeProvider theme={themeSwitch.theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <TempPasswordProvider>
            <Outlet />
          </TempPasswordProvider>
        </QueryClientProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <div>Something went wrong.</div>;
};

export default withSentry(function App() {
  return (
    <ThemeSwitchProvider>
      <AppContent />
    </ThemeSwitchProvider>
  );
});

export function HydrateFallback() {
  return null;
}
